import { BlotterErrorIcon } from 'components/Blotters/BlotterErrorIcon';
import { BlotterSpinner } from 'components/Blotters/BlotterSpinner';
import { AdvancedFilterButton } from 'components/Blotters/Filter/AdvancedFilterButton';
import { BlotterTitleContainer, BlotterTitleLabel } from 'components/Blotters/Filter/blotter-title';
import { ExportCsv } from 'components/Blotters/Filter/ExportCsv';
import type { StringFileResponse } from 'models/FileResponse';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { map } from 'rxjs/operators';
import { downloadCsv } from 'utils/documents/downloadDocument';

import { ActivityFilter } from '../../Filter/ActivityFilter';
import { DateFilter } from '../../Filter/DateFilter';
import { FullTextSearch } from '../../Filter/full-text-search';
import type { FilterProps } from './Filter.connect';
import { connectFilterComponent } from './Filter.connect';
import { withFetchCsvApi } from './Filter.withFetchCsvApi';
import { PriceImprovementAdvancedFilters } from './PriceImprovementAdvancedFilters';
import { StatusFilter } from './StatusFilter';

export const PriceImprovementBlotterFilterRaw: FC<FilterProps> = ({
  activityFilter,
  statusFilter,
  dateFilter,
  quickSearchTerm,
  loading,
  error,
  advancedFilters,
  startDate,
  endDate,
  requestParams,
  totalItems,
  fetchCSV,
  onStartDateFilterChanged,
  onEndDateFilterChanged,
  onActivityFilterChanged,
  onStatusFilterChanged,
  onDateFilterChanged,
  onQuickSearchTermChanged,
  onAdvancedFilterToggled,
}) => {
  const { formatMessage } = useIntl();
  const fetch = useCallback(() => {
    return fetchCSV({ ...requestParams, page: 0, size: totalItems || 0 }).pipe(
      map((response: StringFileResponse) => {
        downloadCsv(response.data, response.filename);
      }),
    );
  }, [fetchCSV, requestParams, totalItems]);

  return (
    <div className="">
      <div className="w-100 d-flex flex-row flex-wrap flex-md-nowrap border-top">
        <BlotterTitleContainer className="flex-grow-1">
          <BlotterTitleLabel>My price improvements</BlotterTitleLabel>
          {error && <BlotterErrorIcon message={formatMessage({ id: 'blotter.filter.label.error' })} />}
          <BlotterSpinner className="flex-shrink-0 ms-2" hide={!loading} />
        </BlotterTitleContainer>
        <ActivityFilter value={activityFilter} onSelect={onActivityFilterChanged} />
        <StatusFilter value={statusFilter} onSelect={onStatusFilterChanged} />
        <DateFilter value={dateFilter} onChange={onDateFilterChanged} />
        <FullTextSearch value={quickSearchTerm} onChange={onQuickSearchTermChanged}>
          <ExportCsv fetch={fetch} className="d-inline-block" />
          <AdvancedFilterButton value={advancedFilters} onToggle={onAdvancedFilterToggled} />
        </FullTextSearch>
      </div>
      {advancedFilters && (
        <PriceImprovementAdvancedFilters
          startDate={startDate}
          onStartDateFilterChanged={onStartDateFilterChanged}
          endDate={endDate}
          onEndDateFilterChanged={onEndDateFilterChanged}
        />
      )}
    </div>
  );
};

export const PriceImprovementBlotterFilter = withFetchCsvApi(connectFilterComponent(PriceImprovementBlotterFilterRaw));
